import {
  Button,
  Card,
  DialogProvider,
  Icon,
  Input,
  Tabs,
  useAlert,
} from "@storoey/sui";
import { createEffect, createSignal, For, Show } from "solid-js";
import { FileDropZone } from "../../components/FileDropZone/FileDropZone";
import { Topic } from "../../models/excel.model";
import { databaseService } from "../../services/database.service";
import { parserService } from "../../services/parser.service";
import { DialogProps } from "../../types/dialog.props";

const isValidExcelFile = (file: File) => {
  const fileName = file.name.toLowerCase();
  const isExcelByExtension =
    fileName.endsWith(".xlsx") ||
    fileName.endsWith(".xltx") ||
    fileName.endsWith(".xls") ||
    fileName.endsWith(".xlsb") ||
    fileName.endsWith(".xlam");

  const isExcelByMime =
    file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
    file.type ===
      "application/vnd.openxmlformats-officedocument.spreadsheetml.template" ||
    file.type === "application/vnd.ms-excel" ||
    file.type === "application/vnd.ms-excel.sheet.binary.macroEnabled.12" ||
    file.type === "application/vnd.ms-excel.addin.macroEnabled.12";

  return isExcelByExtension || isExcelByMime;
};

export const CreateTemplateDialog = (props: {} & DialogProps) => {
  const alert = useAlert();

  let dropzoneController: { reset: () => void } = { reset: () => {} };

  const [isLoading, setIsLoading] = createSignal(false);
  const [isSaving, setIsSaving] = createSignal(false);
  const [fileInfo, setFileInfo] = createSignal<File>();
  const [templates, setTemplates] = createSignal<
    {
      sheetName: string;
      topics: Topic[];
    }[]
  >([]);
  const [tabIndex, setTabIndex] = createSignal(0);

  const currentTemplate = () => templates()[tabIndex()];

  const handleFiles = async (files: FileList | null) => {
    try {
      setIsLoading(true);

      if (files === null) {
        setFileInfo(undefined);
        setTemplates([]);
        setTabIndex(0);
        return;
      }

      if (files.length === 0) {
        return;
      }

      const file = Array.from(files)[0];

      if (!isValidExcelFile(file)) {
        throw new Error("Invalid file");
      }

      const parsedTemplates = await parserService.parseExcelToJson(file);

      setFileInfo(file);
      setTemplates(parsedTemplates.slice());
      setTabIndex(0);
    } catch (err: unknown) {
      dropzoneController.reset();
      if (err instanceof Error) {
        if (err.message === "Invalid file") {
          alert.notify({
            title: "Feil",
            text: "Ugyldig fil",
            variant: "outline",
            color: "red",
            icon: "error",
            dismissOnClick: true,
          });
        } else {
          alert.notify({
            title: "Feil",
            text: err.message,
            variant: "outline",
            color: "red",
            icon: "error",
            dismissOnClick: true,
          });
        }
      }
    } finally {
      setIsLoading(false);
    }
  };

  const onSave = async () => {
    try {
      setIsSaving(true);
      await new Promise(async (resolve, reject) => {
        const file = fileInfo();

        if (!file) {
          return reject("No file");
        }

        const hash = await parserService.hashFileSHA256(file);
        for (const template of templates()) {
          await databaseService.storeNewTemplate(
            file.name,
            file.lastModified,
            hash,
            template.sheetName,
            template.topics
          );
        }

        resolve({});
      });
      setIsSaving(false);
      props.close();
    } catch (err: unknown) {
      if (err instanceof Error) {
        alert.notify({
          title: "Feil",
          text: err.message,
          variant: "outline",
          color: "red",
          icon: "error",
          dismissOnClick: true,
        });
      }
      setIsSaving(false);
    } finally {
    }
  };

  createEffect(() => {
    if (!props.isOpen()) {
      setFileInfo(undefined);
      setTemplates([]);
      dropzoneController.reset();
    }
  });

  createEffect(() => {
    const list = templates();
    if (list.length === 0) return;
    if (tabIndex() < 0 || tabIndex() >= list.length) {
      setTabIndex(0);
    }
  });

  return (
    <DialogProvider
      open={props.isOpen}
      close={() => (isSaving() ? undefined : props.close())}
    >
      <DialogProvider.Content>
        <Card>
          <Card.Content>
            <Card.Title>Legg til male</Card.Title>
            <Card.Text>Last opp excel-fil for å lage ny mal</Card.Text>
          </Card.Content>
          <Card.Actions>
            <Show when={currentTemplate()} keyed>
              <div>
                <Tabs>
                  <For each={templates()}>
                    {(template, index) => (
                      <Tabs.Tab
                        disabled={isSaving()}
                        active={index() === tabIndex()}
                        onClick={() => setTabIndex(index())}
                      >
                        {template.sheetName}
                      </Tabs.Tab>
                    )}
                  </For>
                </Tabs>
                <div>
                  <Input
                    leftIcon={<Icon>fingerprint</Icon>}
                    label="Navn"
                    disabled={isSaving()}
                    value={currentTemplate()?.sheetName ?? ""}
                    onChange={(eventOrValue: unknown) => {
                      const nextValue = (() => {
                        if (typeof eventOrValue === "string")
                          return eventOrValue;

                        const evt = eventOrValue as Event | undefined;
                        const currentTarget = evt?.currentTarget as
                          | HTMLInputElement
                          | null
                          | undefined;
                        if (currentTarget?.value !== undefined) {
                          return currentTarget.value;
                        }

                        const target = evt?.target as
                          | HTMLInputElement
                          | null
                          | undefined;
                        if (target?.value !== undefined) {
                          return target.value;
                        }

                        return "";
                      })();

                      setTemplates((prev) =>
                        prev.map((template, i) =>
                          i === tabIndex()
                            ? { ...template, sheetName: nextValue }
                            : template
                        )
                      );
                    }}
                  />
                </div>
              </div>
            </Show>

            <FileDropZone
              accept=".xlsx"
              file={fileInfo()}
              onFilesSelected={handleFiles}
              controller={dropzoneController}
              isLoading={isLoading()}
            />
          </Card.Actions>
          <Card.Actions align="end">
            <Button
              variant="ghost"
              color="red"
              disabled={isSaving() || isLoading()}
              onclick={props.close}
            >
              Avbryt
            </Button>
            <Button
              variant="default"
              color="green"
              disabled={isSaving() || isLoading() || templates().length === 0}
              onclick={onSave}
            >
              Lagre
            </Button>
          </Card.Actions>
        </Card>
      </DialogProvider.Content>
    </DialogProvider>
  );
};
